import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import Animated from 'react-native-reanimated';
import { noop } from 'lodash';
import { getTransactions } from 'actions';
import { getTransactionsFlatlistData, } from 'utils/getTransactions';
import Button, { HEIGHT } from 'design-system/Button';
import * as types from 'actions/types';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Indicator from 'design-system/Indicator';
import { AppView, Spacer } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import ListLoader from 'design-system/ListLoader';
import ListEmptyView from 'design-system/ListEmptyView';
import TransactionRow from 'features/transactions/components/TransactionRow';
import Modal from 'utils/packages/Modal';
import { formatPlural } from 'utils';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.id.toString();
const getItemType = (item) => {
    if ('sectionTitle' in item) {
        return 'sectionHeader';
    }
    return 'row';
};
const GroupSelectTransactionsScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const dispatch = useAppDispatch();
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const { isMultipleSelect, type, onContinue } = route.params;
    const [isFetching, setIsFetching] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1000);
    const [selected, setSelected] = useState([]);
    useEffect(() => {
        setIsFetching(true);
        dispatch(getTransactions({ page: 1, type })).then((response) => {
            if (response?.type === types.TRANSACTIONS_SUCCESS) {
                setPageCount(response?.payload?.paging?.pageCount || 0);
                setPage(response?.payload?.paging?.page || 1);
                const filtered = response?.payload?.transactions?.filter((tr) => !tr?.isPending);
                setIsFetching(false);
                setTransactions(filtered || []);
            }
        });
    }, [type]);
    const onEndReached = useCallback(() => {
        if (page < pageCount) {
            dispatch(getTransactions({ page: page + 1, type })).then((response) => {
                if (response?.type === types.TRANSACTIONS_SUCCESS) {
                    setPage(response?.payload?.paging?.page || page + 1);
                    if (response?.payload?.transactions) {
                        const filtered = response.payload.transactions.filter((tr) => !tr?.isPending);
                        const newTransactions = [...transactions, ...filtered];
                        setTransactions(newTransactions);
                    }
                }
            });
        }
    }, [page, pageCount, transactions, type]);
    const onSelect = useCallback((item) => {
        setSelected((prev) => {
            if (!isMultipleSelect) {
                return [item];
            }
            if (prev.find((t) => t.id === item.id)) {
                return prev.filter((i) => i.id !== item.id);
            }
            return [...prev, item];
        });
    }, [isMultipleSelect]);
    const [isContinuing, setIsContinuing] = useState(false);
    const onPress = useCallback(() => {
        if (onContinue) {
            onContinue(selected, setIsContinuing);
        }
        else {
            navigation.navigateDeprecated('GroupExpense', {
                groupId: route.params.groupId,
                transactions: selected,
            });
        }
    }, [onContinue, route.params.groupId, selected]);
    const sectionedData = useMemo(() => {
        if (isFetching && !transactions.length)
            return [];
        return getTransactionsFlatlistData(transactions, false, true);
    }, [isFetching, transactions]);
    const renderItem = useCallback(({ item }) => {
        const isInGroup = 'spendingGroupInfo' in item &&
            !!item.spendingGroupInfo?.groups?.some((g) => g.group.id === route.params.groupId);
        const openItem = () => {
            if (isInGroup) {
                Modal.showError('This transaction is already added to this group', undefined, 'Added transaction');
            }
            else {
                onSelect(item);
            }
        };
        return (<TransactionRow item={item} isEditing openItem={openItem} isSelected={!!selected.find((t) => t.id === item.id)} isMultipleSelect={isMultipleSelect} rowStyle={isInGroup ? styles.inGroup : undefined} activeOpacity={isInGroup ? 0.5 : 1} guessedHomeCountry={guessedHomeCountry} onPressUpgradeForSearch={noop} onCompare={noop} openSubscription={noop} blurSubscriptionValues={false}/>);
    }, [
        guessedHomeCountry,
        isMultipleSelect,
        onSelect,
        route.params.groupId,
        selected,
    ]);
    const ListEmptyComponent = useMemo(() => (<View>
            {isFetching ? (<ListLoader />) : (<>
                <Spacer.H16 />
                <ListEmptyView onCard title="No transactions" subTitle="There are no transactions in your account"/>
              </>)}
          </View>), [isFetching]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          {isFetching ? (<View style={styles.loaderWrap}>
              <Indicator dark includeHeaderHeight/>
            </View>) : (<>
              <AnimatedFlashList extraData={selected} onScroll={onScroll} data={sectionedData} renderItem={renderItem} scrollEventThrottle={16} onEndReachedThreshold={0.8} keyExtractor={keyExtractor} onEndReached={onEndReached} ListEmptyComponent={ListEmptyComponent} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} getItemType={getItemType} contentContainerStyle={contentContainerStyle}/>
              <Button title={!selected.length
                ? 'Add transaction'
                : formatPlural('Add transaction', `Add (${selected.length}) transactions`, selected.length)} brand floating disabled={!selected.length} isFetching={isContinuing} onPress={onPress}/>
            </>)}
        </AppView>);
}, ({ colors }) => ({
    title: 'Add transactions',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    loaderWrap: {
        width: '100%',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    inGroup: {
        opacity: 0.5,
    },
});
export default GroupSelectTransactionsScreen;
